import { useState } from "react";
import ListItem from "@/components/ListItem";
import Avatar from "@/components/Avatar";
import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { MentionableTextField, AtMentionTag, parseMentions, splitMentionName } from "@/components/AtMention";
import type { CommentData } from "./types";

interface Props {
  comment: CommentData;
  isEditing: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  compact?: boolean;
  onEdit?: (id: string) => void;
  onSave?: (id: string, content: string) => void;
  onCancel?: (id: string) => void;
  onDelete?: (id: string) => void;
  onOpenMenu?: (commentId: string, actions: Action[]) => void;
}

function isEdited(comment: CommentData): boolean {
  return !!comment.updatedAt && comment.updatedAt !== comment.createdAt;
}

function CommentContent({ content }: { content: string }) {
  const segments = parseMentions(content);
  const hasMentions = segments.some((s) => s.type === "mention");
  if (!hasMentions) return <>{content}</>;

  return (
    <>
      {segments.map((segment, i) =>
        segment.type === "text" ? (
          <span key={i}>{segment.text}</span>
        ) : (
          <AtMentionTag
            key={i}
            user={{
              id: segment.userId,
              ...splitMentionName(segment.name),
              email: ""
            }}
          />
        )
      )}
    </>
  );
}

export default function Comment({
  comment,
  isEditing,
  canEdit,
  canDelete,
  compact = false,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onOpenMenu
}: Props) {
  const [editContent, setEditContent] = useState(comment.content);

  const formattedDate = toDateTimeFormat(comment.createdAt, "MMM dd, yyyy h:mm a");
  const edited = isEdited(comment);

  const handleSave = () => {
    onSave?.(comment.id, editContent);
  };

  const handleCancel = () => {
    setEditContent(comment.content);
    onCancel?.(comment.id);
  };

  const showActions = !!(canEdit || canDelete) && !!onOpenMenu;

  const handleMenuClick = () => {
    const actions: Action[] = [];
    if (canEdit) {
      actions.push({
        text: "Edit Comment",
        onClick: () => {
          setEditContent(comment.content);
          onEdit?.(comment.id);
        },
        testId: "comment-menu-edit"
      });
    }
    if (canDelete) {
      actions.push({
        text: "Delete Comment",
        iconName: "delete",
        destructive: true,
        onClick: () => {
          onDelete?.(comment.id);
        },
        testId: "comment-menu-delete"
      });
    }
    if (actions.length === 0) return;
    onOpenMenu?.(comment.id, actions);
  };

  const dateDisplay = (
    <span data-testid="comment-date">
      {formattedDate}
      {edited && (
        <span className="ml-1 italic" data-testid="comment-edited-indicator">
          (edited)
        </span>
      )}
    </span>
  );

  if (isEditing) {
    return (
      <div className="pt-6 pb-4" data-testid={`comment-${comment.id}`}>
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <Avatar picture={comment.author.photo} size="medium" />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-3">
            <MentionableTextField
              value={editContent}
              onChange={setEditContent}
              rows={3}
              className="w-full"
              label="Edit Comment"
              autoFocus
              data-testid="comment-edit-field"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="text" onClick={handleCancel} testId="comment-cancel-button">
                Cancel
              </Button>
              <Button variant="filled" onClick={handleSave} disabled={!editContent.trim()} testId="comment-save-button">
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative ${compact ? "" : "py-2"} ${showActions ? "pr-10" : ""}`}
      data-testid={`comment-${comment.id}`}
    >
      <ListItem
        useAvatar
        picture={comment.author.photo}
        overline={comment.author.name}
        headline={<CommentContent content={comment.content} />}
        supportingText={dateDisplay}
        longContent
        compact={compact}
      />
      {showActions && (
        <div className="absolute top-1/2 -translate-y-1/2 right-0 flex items-center">
          <IconButton
            id={actionId(comment.id)}
            name="more_vert"
            onClick={handleMenuClick}
            ariaLabel="Comment actions"
            data-testid="comment-actions-button"
            iconClassName="font-extrabold"
            skipMargin
          />
        </div>
      )}
    </div>
  );
}
